// Imports
import { faArrowRotateLeft, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import styled, { css } from 'styled-components/macro';
import formatForDatetimeLocalInput from '../../../../../../shareables/front-end/utils/format-for-datetime-local-input';
import Button from '../../../../../../shareables/foundation/front-end/components/buttons/standard';
import File from '../../../../../../shareables/foundation/front-end/components/forms/fields/file';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Select from '../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import Textarea from '../../../../../../shareables/foundation/front-end/components/forms/fields/textarea';
import commonActions from '../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetListingResponse } from '../../../../../../shareables/types/api/main/website/page/type/216/listing/get';
import { PostListingParameters } from '../../../../../../shareables/types/api/main/website/page/type/216/listing/post';
import { PutListingParameters } from '../../../../../../shareables/types/api/main/website/page/type/216/listing/put';
import { GetTagsResponse } from '../../../../../../shareables/types/api/main/website/page/type/216/tags/get';
import { visuallyHiddenLabelStyle } from '../../../../shared-components/visually-hidden-label';
import Panel from '../shared-components/panel';
import useMemberCheck from '../../../../../../utils/use-member-check';


// Styled components
const ImageFormContainer = styled.div`
	margin-bottom: 1.25rem;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.75rem;
	
	> div {
		flex: 1 1 0;
	}
`;

const ImageButtonContainer = styled.div<{ $hideLabel?: boolean }>`
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	
	div {
		margin-bottom: 0 !important;
	}
	
	${({ $hideLabel }) =>
		$hideLabel &&
		css`
			label {
				${visuallyHiddenLabelStyle}
			}
		`}
`;

const Image = styled.img<{ removeImage: boolean }>`
	${({ removeImage }) => removeImage && 'opacity: 0.5;'}
`;

const RemovePhotoButton = styled(Button)`
	svg.svg-inline--fa {
		margin-right: 0.25em;
	}
`;


// Define the accepted props
interface Props {
	pageID: number;
	isPanelOpen: boolean;
	onPanelClose: () => void;
	onFormCompletion: () => void;
	storybookTags?: GetTagsResponse['tags'];
	listing?: GetListingResponse;
	listingID?: string;
	storybookRemoveImage?: true;
}


// Function component
const ListingForm: React.FC<Props> = ({
	pageID,
	isPanelOpen,
	onPanelClose,
	onFormCompletion,
	storybookTags,
	listing,
	listingID,
	storybookRemoveImage,
}) => {
	// Use state
	const [tags, setTags] = useState<GetTagsResponse['tags'] | null>(storybookTags ?? null);
	const [removeImage, setRemoveImage] = useState(storybookRemoveImage ?? false);
	const [formKey, setFormKey] = useState(0);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const member = useAppSelector((state) => state.self.member);
	const signedIn = useAppSelector((state) => state.common.user.signedIn);
	const communityTimezone = useAppSelector((state) => state.self.website?.formatting.timezone);
	const isMember = useMemberCheck({ type: 'MEMBER' });
	
	
	// Determine local timezone
	const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	
	
	// Function that loads tags
	const memoizedLoadTags = useCallback(() => {
		if (isMember) {
			void dispatch(
				apiActions.call<undefined, undefined, GetTagsResponse>({
					action: 'GET',
					uri: `/website/${window.location.hostname}/page/id_${pageID}/type/216/tags`,
					completion: (json) => {
						setTags(json.response.tags);
					},
				})
			);
		}
	}, [dispatch, pageID, isMember]);
	
	
	// Load tags on mount
	useEffect(() => {
		memoizedLoadTags();
	}, [memoizedLoadTags]);
	
	
	// Reset form when panel closes
	useEffect(() => {
		if (isPanelOpen) {
			return;
		}
		
		setFormKey((previousKey) => previousKey + 1);
		setRemoveImage(false);
	}, [isPanelOpen, pageID]);
	
	
	// If signed in but not a member or global sign in user, close the form
	useEffect(() => {
		if (isPanelOpen && signedIn && !isMember) {
			onPanelClose();
		}
	}, [isPanelOpen, signedIn, isMember, onPanelClose]);
	
	
	// If loading or not a member or global sign in user, display nothing
	if (isMember ? tags === null : signedIn) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<PostListingParameters | PutListingParameters, undefined, undefined> = {
		action: listing ? 'PUT' : 'POST',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/type/216/listing${listingID ? `/${listingID}` : ''}`,
		submitButton: {
			text: listing ? 'Save' : 'Post',
		},
		extraParameters: {
			removePhoto: removeImage ? true : undefined,
		},
		recoverState: !listing,
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		// Display page message
		dispatch(
			commonActions.showPageMessage({
				title: 'Success!',
				message: `Your listing has been ${listing ? 'updated' : 'posted'}.`,
				color: 'success',
			})
		);
		
		
		// Reload tags
		memoizedLoadTags();
		
		
		// Handle form completion logic
		onFormCompletion();
		
		
		// Close panel
		onPanelClose();
	};
	
	
	// Return JSX
	return (
		<AnimateHeight height={isPanelOpen ? 'auto' : 0} duration={400} animateOpacity>
			<Panel closeOnClick={onPanelClose} signInText='posting a listing'>
				<APIForm options={formOptions} onCancel={onPanelClose} onCompletion={handleFormCompletion} key={formKey}>
					<Input
						type='text'
						name='title'
						label='Title'
						maxLength={40}
						placeholder='Something short and descriptive'
						required
						initialValue={listing?.title}
					/>
					
					<Textarea name='text' label='Listing text' rows={8} required initialValue={listing?.text} />
					
					<Textarea
						name='contactInfo'
						label='Contact information'
						rows={2}
						placeholder='How can people contact you about this listing?'
						note='Optional'
						initialValue={listing?.contactInfo}
					/>
					
					<Select name='tag' label='Tag your listing' initialValue={listing?.tag} required placeholder='Please select…'>
						{tags?.map((tag) => (
							<option value={tag} key={tag}>
								{tag}
							</option>
						))}
					</Select>
					
					<ImageFormContainer>
						{listing?.thumbnail && <Image src={listing.thumbnail} alt='' removeImage={removeImage} />}
						
						<ImageButtonContainer $hideLabel={Boolean(listing?.thumbnail)}>
							{!removeImage && (
								<File
									label='Attach a photo'
									name='photo'
									accept='image/*'
									note={listing?.thumbnail ? undefined : 'Optional'}
									prompt={listing?.thumbnail ? 'Change photo' : undefined}
								/>
							)}
							
							{listing?.thumbnail && (
								<RemovePhotoButton
									type='button'
									color='danger'
									onClick={() => setRemoveImage((oldRemoveImage) => !oldRemoveImage)}
								>
									<FontAwesomeIcon icon={removeImage ? faArrowRotateLeft : faTrash} />{' '}
									{removeImage ? 'Restore photo' : 'Remove photo'}
								</RemovePhotoButton>
							)}
						</ImageButtonContainer>
					</ImageFormContainer>
					
					<Select
						name='identifier'
						label='Your name'
						initialValue={
							listing && member
								? (Object.entries(member.names)
										.find(([, name]) => name === listing.submitter?.formattedName)?.[0]
										.toUpperCase() ?? 'MEMBER')
								: undefined
						}
						required
					>
						<option value='MEMBER'>{member?.names.member}</option>
						{member?.names.member !== member?.names.account && <option value='ACCOUNT'>{member?.names.account}</option>}
						<option value='FAMILY'>{member?.names.family}</option>
					</Select>
					
					<Input
						type='datetime-local'
						name='expiresAt'
						label='Expiration'
						note={`Optional${communityTimezone && communityTimezone !== localTimezone ? '; your local timezone' : ''}`}
						min={formatForDatetimeLocalInput(new Date(Date.now()))}
						initialValue={listing?.expiresAt ? formatForDatetimeLocalInput(listing.expiresAt) : undefined}
					/>
				</APIForm>
			</Panel>
		</AnimateHeight>
	);
};

export default ListingForm;
